/**
 * exportRunSummary — what THIS export run did, aggregated.
 *
 * Replaces the Add panel's per-contact status badges. The panel stopped being a
 * roster (Trello #758): per-contact outcomes live in `ContactsTable`, which
 * already carries an outcome column and a Thrive ID column, and the panel now
 * answers the one question it exists to ask — what happened.
 *
 * Pure, so every branch is table-testable without driving a run.
 *
 * THE SCOPING IS THE WHOLE POINT. `ExportStatus.results` is CUMULATIVE — every
 * contact ever exported on this import — so counting it whole announces totals:
 * adding 3 to a list with 12 already exported reports 15. `useExportToThrive`
 * has carried that filter inline since cl.4; this module is the one definition
 * of it, so the toast and the panel can never disagree about the same run.
 */

import type { ContactExportResult } from "@nu-assist/contracts";

export interface ExportRunScope {
  /**
   * THE DAY NAMED HERE ARRIVED, and this is what it cost.
   *
   * `contactIds` used to mean "the ids this run submitted", and a select-all
   * run had none — so a flag said "take the whole payload instead". That was
   * sound while a select-all covered every row of the import, and the comment
   * here predicted its own end: "the day a filtered select-all ships, 'every
   * row in the payload belongs to this run' becomes false and this arm silently
   * over-counts". This PR shipped that day twice, and a run that dispatched 2
   * rows reported "Added 14 of 14".
   *
   * BOTH ARMS NOW FILTER BY `contactIds`, so there is no arm to select and the
   * flag that selected one is gone. What differs is only where the ids come
   * from, and that is the caller's business: an explicit run knows them, a
   * select-all run observes them from what changed since it was fired. The flag
   * survived one round as "it still picks the id set" — it does not; the caller
   * picks the set with its own ternary, one line above where it was passed, and
   * `exportRunSummary` never read it (/pr-review iter 2, Agent A L1, who
   * proved it by inverting the value at its only call site and watching 2,805
   * tests stay green).
   *
   * THE ROWS THIS RUN COVERED. Submitted for an explicit selection; for a
   * select-all, observed from what CHANGED since the run was fired — not from
   * the `pending` stamps, which a second JSDoc block over this same member
   * still claimed after the rule had moved on. Two blocks over one field, the
   * second contradicting the first, is the shape flagged one round earlier in
   * another file and reproduced here by the commit that fixed it
   * (/pr-review iter 3, Agent A N4).
   */
  contactIds: ReadonlySet<string>;
}

export interface ExportRunSummary {
  exported: number;
  failed: number;
  skipped: number;
  /** Rows still `pending` at terminal. Counted, not dropped — see below. */
  pending: number;
  /**
   * Rows in scope. `exported + failed + skipped + pending === total`.
   *
   * THIS IS WHAT THE RUN COVERED, which is not always the number the
   * pre-run sentence promised. On a cross-page `select_all` the SERVER resolves
   * the scope, so the payload can carry more rows than the client's
   * `selectAllScope.total` quoted — and the screen reads "about to add N" then
   * "Added X of N+k" (/pr-review iter 3, Agent C M3).
   *
   * Reporting the resolved count is the right call and deliberately not
   * clamped: the alternative is a denominator that hides rows the run actually
   * touched, which is the over-promise this whole panel exists to stop. The
   * pre-run number is a claim about a scope; this one is a fact about a run.
   */
  total: number;
  /**
   * `partial` the moment ANY row failed. The server calls a run `succeeded`
   * whenever any row on the import is exported, so a run with failures reaches
   * the UI wearing a success — and a heading that reads as a clean success over
   * a run that lost contacts is the failure mode this field exists to prevent.
   *
   * Skipped rows do NOT make a run partial: a W1 duplicate is skipped by
   * design, and reporting the rule working as a fault would train recruiters to
   * ignore the warning.
   */
  status: "succeeded" | "partial";
}

/**
 * `null` when the payload carries none of this run's rows.
 *
 * Deliberately not "fall back to the whole set". An empty scope means the
 * payload does not carry this run's rows YET, which is precisely when the
 * cumulative total is the wrong number — the caller must render an
 * outcome-unknown state rather than a plausible one it cannot support.
 */
export function exportRunSummary(
  results: readonly ContactExportResult[],
  scope: ExportRunScope
): ExportRunSummary | null {
  // ONE RULE FOR BOTH ARMS. The select-all arm used to take `results` whole,
  // which was the over-count; its ids are now observed rather than absent, so
  // there is nothing left for the branch to do.
  const scoped = results.filter((r) => scope.contactIds.has(r.contact_id));
  if (scoped.length === 0) return null;

  const count = (s: ContactExportResult["export_status"]) =>
    scoped.filter((r) => r.export_status === s).length;
  const failed = count("failed");

  return {
    exported: count("exported"),
    failed,
    skipped: count("skipped"),
    pending: count("pending"),
    total: scoped.length,
    status: failed > 0 ? "partial" : "succeeded",
  };
}
